import type { Apartment } from '../../api.apartments';
import type { SectionKey } from './groups';

/**
 * Which edits are waiting for the shared "Uložit". Compares the form with the apartment as last
 * loaded from the server, field by field, for exactly the fields that button sends — no more.
 *
 * <p>Separate from the page so the rules are testable: the Ubyport password sentinel, `null` vs
 * `''`, and objects that the page rebuilds on load (`paymentMethod`, `cityTaxContact`, ...) must not
 * make a pristine form look edited.
 */

export const PASSWORD_SENTINEL = '••••••••';

/** Form field -> the section a host would say it belongs to. */
export const FIELD_SECTION: Partial<Record<keyof Apartment, SectionKey>> = {
  internalName: 'general', displayName: 'general', address: 'general', capacity: 'general',
  description: 'general', isActive: 'general', publicCheckinWindowDays: 'general', documentScanEnabled: 'general',

  assignedContact: 'contact', contactFullName: 'contact', contactEmail: 'contact', contactPhone: 'contact',
  guestFooterContactType: 'contact',

  ubyportUsername: 'ubyport', ubyportPassword: 'ubyport', idub: 'ubyport', ubyportShortName: 'ubyport',
  hostFullName: 'ubyport', hostPhone: 'ubyport', autoSendToUbyport: 'ubyport',
  ubyportAutoSendMaxGuests: 'ubyport', ubyportAutoSendDelayDays: 'ubyport',

  paymentMethodId: 'payments',

  cityTaxCollectionEnabled: 'citytax', cityTaxBankAccount: 'citytax', cityTaxVariableSymbol: 'citytax',
  cityTaxContactId: 'citytax', cityTaxMailboxId: 'citytax', cityTaxMailboxOtherName: 'citytax',
  cityTaxMailboxOtherDataboxId: 'citytax', cityTaxMailboxOtherAddress: 'citytax',
  cityTaxAdult: 'citytax', cityTaxChild: 'citytax', cityTaxDisabled: 'citytax',

  // Edited from the guide (lock choice lives on the apartment) and carried by both save buttons.
  ttlockEnabled: 'guide', ttlockLockId: 'guide',
};

/** null, undefined and '' all mean "empty" in this form. */
const norm = (v: unknown): unknown => (v === null || v === undefined || v === '' ? '' : v);

const BOOLEAN_FIELDS: (keyof Apartment)[] = [
  'isActive', 'documentScanEnabled', 'autoSendToUbyport', 'cityTaxCollectionEnabled', 'ttlockEnabled',
];

function valueOf(a: Apartment, key: keyof Apartment): unknown {
  if (key === 'assignedContact') return norm(a.assignedContact?.id);
  if (key === 'ubyportPassword') {
    const p = a.ubyportPassword;
    return !p || !p.trim() || p === PASSWORD_SENTINEL ? '' : p;
  }
  // The server may omit a flag that the page later sets to `false`: same thing.
  if (BOOLEAN_FIELDS.includes(key)) return !!a[key];
  return norm(a[key]);
}

/** Page-built mirrors of an id field; they change together with it, so they borrow its section. */
const MIRROR_SECTION: Partial<Record<keyof Apartment, SectionKey>> = {
  paymentMethod: 'payments',
  cityTaxContact: 'citytax',
  cityTaxMailbox: 'citytax',
};

/**
 * Sections with at least one edited field, in field order. Empty = nothing to save.
 *
 * <p>A field this file has never heard of (somebody added one to the form) is still compared, and
 * counts under `general`. A wrong section label is a cosmetic flaw; an edit that never shows as
 * unsaved would be a lost change.
 */
export function dirtySections(form: Apartment | null, saved: Apartment | null): SectionKey[] {
  if (!form || !saved) return [];
  const out: SectionKey[] = [];
  const keys = new Set<keyof Apartment>([
    ...(Object.keys(FIELD_SECTION) as (keyof Apartment)[]),
    ...(Object.keys(form) as (keyof Apartment)[]),
    ...(Object.keys(saved) as (keyof Apartment)[]),
  ]);
  keys.forEach(key => {
    const section = FIELD_SECTION[key] ?? MIRROR_SECTION[key] ?? 'general';
    if (out.includes(section)) return;
    if (JSON.stringify(valueOf(form, key)) !== JSON.stringify(valueOf(saved, key))) out.push(section);
  });
  return out;
}
